import { useEffect, useState } from 'react'
import type { FormEvent } from 'react'
import { useAuth } from '../auth/ContextoAuth'
import { configuracion as apiConfiguracion, respaldos as apiRespaldos } from '../api/servicios'
import { descargar, ErrorApi } from '../api/cliente'
import { fechaHora } from '../api/formato'
import { fijarMonedaNegocio } from '../api/moneda'
import { fijarZonaNegocio } from '../api/zona'
import type { Configuracion as DatosConfiguracion, Region } from '../api/tipos'
import { Estado } from '../componentes/Estado'
import { mensajeDeError, usarCargar } from '../componentes/usarCargar'
import './listados.css'

export function Configuracion() {
    const { puede } = useAuth()

    return (
        <div className="pagina-listado columna g4">
            <h1>Configuración</h1>

            <DatosDelNegocio />

            {puede('respaldo.ejecutar') && <Respaldos />}
        </div>
    )
}

/* -------------------------------------------------------------------------- */
/* Datos del negocio                                                           */
/* -------------------------------------------------------------------------- */

function DatosDelNegocio() {
    const { puede } = useAuth()
    const editable = puede('configuracion.editar')
    const datos = usarCargar(() => apiConfiguracion.obtener(), [])

    return (
        <section className="panel columna g3">
            <h2>Datos del negocio</h2>
            <p className="tenue chico">Es lo que sale impreso en el encabezado del ticket.</p>

            <Estado cargando={datos.cargando} error={datos.error} alReintentar={datos.recargar}>
                {datos.datos && (
                    <>
                        <SelectorRegion
                            actual={datos.datos}
                            editable={editable}
                            alAplicar={datos.fijar}
                        />

                        <hr className="separador" />

                        {/* La `key` fuerza a rearmar el formulario cuando se
                            aplica una region: sus campos son estado local, y sin
                            esto seguirian mostrando el redondeo y el impuesto de
                            antes mientras el servidor ya guardo los nuevos. */}
                        <FormularioConfiguracion
                            key={datos.datos.actualizadoEn}
                            inicial={datos.datos}
                            editable={editable}
                            alGuardar={datos.fijar}
                        />
                    </>
                )}
            </Estado>
        </section>
    )
}

/* -------------------------------------------------------------------------- */
/* Region                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Elegir el pais deja lista la moneda, los decimales, el redondeo, el impuesto
 * y la zona horaria de una vez.
 *
 * Es un **preset y no un candado**: despues de aplicarlo, cada campo sigue
 * editable abajo. Lo que no se ofrece suelto es la direccion del redondeo,
 * porque no es una preferencia del comerciante sino una regla del pais, y una
 * casilla la invita a quedar mal puesta.
 */
function SelectorRegion({
    actual,
    editable,
    alAplicar,
}: {
    actual: DatosConfiguracion
    editable: boolean
    alAplicar: (datos: DatosConfiguracion) => void
}) {
    const regiones = usarCargar(() => apiConfiguracion.regiones(), [])
    const [elegida, setElegida] = useState(actual.region ?? '')
    const [error, setError] = useState<string | null>(null)

    // Cuando el servidor pide confirmacion porque ya hay ventas, se guarda aqui
    // que region la estaba pidiendo: confirmar tiene que reintentar esa misma y
    // no la que el usuario haya marcado despues.
    const [porConfirmar, setPorConfirmar] = useState<string | null>(null)
    const [enviando, setEnviando] = useState(false)

    async function aplicar(codigo: string, confirmar: boolean) {
        setError(null)
        setEnviando(true)

        try {
            const datos = await apiConfiguracion.aplicarRegion(codigo, confirmar)

            // El formato de toda la aplicacion depende de esto, y se fija una
            // vez por sesion al arrancar. Sin refrescarlo aqui, el cajero
            // seguiria viendo pesos hasta volver a entrar.
            fijarMonedaNegocio(datos)
            fijarZonaNegocio(datos.zonaHoraria)

            setPorConfirmar(null)
            alAplicar(datos)
        } catch (fallo) {
            if (fallo instanceof ErrorApi && fallo.codigo === 'region_con_ventas') {
                setPorConfirmar(codigo)
            }

            setError(mensajeDeError(fallo))
        } finally {
            setEnviando(false)
        }
    }

    const cambia = elegida !== '' && elegida !== actual.region

    return (
        <div className="columna g3">
            <div>
                <h3 className="chico">País donde opera el negocio</h3>
                <p className="tenue chico">
                    Deja lista la moneda, los decimales, el redondeo del efectivo, el impuesto y la
                    zona horaria. Después puede ajustar cada campo por separado.
                </p>
            </div>

            <Estado
                cargando={regiones.cargando}
                error={regiones.error}
                alReintentar={regiones.recargar}
            >
                <fieldset className="sin-marco columna g2" disabled={!editable || enviando}>
                    {(regiones.datos ?? []).map((r) => (
                        <OpcionRegion
                            key={r.codigo}
                            region={r}
                            marcada={elegida === r.codigo}
                            vigente={actual.region === r.codigo}
                            alElegir={() => {
                                setElegida(r.codigo)
                                setPorConfirmar(null)
                                setError(null)
                            }}
                        />
                    ))}

                    {actual.region === null && (
                        <p className="tenue diminuto">
                            Ahora mismo la configuración es manual: {actual.codigoMoneda} con{' '}
                            {actual.decimalesMoneda} decimales. Elegir un país la reemplaza.
                        </p>
                    )}

                    {error && (
                        <div className="aviso aviso-error" role="alert">
                            {error}
                        </div>
                    )}

                    <div className="fila g2">
                        {porConfirmar ? (
                            <button
                                type="button"
                                className="boton boton-peligro"
                                disabled={enviando}
                                onClick={() => void aplicar(porConfirmar, true)}
                            >
                                Entiendo, cambiar de todos modos
                            </button>
                        ) : (
                            <button
                                type="button"
                                className="boton boton-primario"
                                disabled={!cambia || enviando}
                                onClick={() => void aplicar(elegida, false)}
                            >
                                {enviando ? 'Aplicando…' : 'Aplicar país'}
                            </button>
                        )}
                    </div>
                </fieldset>
            </Estado>
        </div>
    )
}

function OpcionRegion({
    region,
    marcada,
    vigente,
    alElegir,
}: {
    region: Region
    marcada: boolean
    vigente: boolean
    alElegir: () => void
}) {
    return (
        <label className="fila g2" style={{ alignItems: 'flex-start' }}>
            <input
                type="radio"
                name="region"
                value={region.codigo}
                checked={marcada}
                onChange={alElegir}
            />
            <span className="columna">
                <span>
                    <strong>{region.nombre}</strong>{' '}
                    {vigente && <span className="tenue chico">(configurado)</span>}
                </span>
                <span className="tenue diminuto">
                    {region.simboloMoneda} {region.codigoMoneda} ·{' '}
                    {region.decimalesMoneda === 0 ? 'sin decimales' : `${region.decimalesMoneda} decimales`}{' '}
                    · {region.nombreImpuesto} {region.impuestoPorcentaje}% · {region.zonaHoraria}
                </span>
                <span className="tenue diminuto">{region.nota}</span>
            </span>
        </label>
    )
}

function FormularioConfiguracion({
    inicial,
    editable,
    alGuardar,
}: {
    inicial: DatosConfiguracion
    editable: boolean
    alGuardar: (datos: DatosConfiguracion) => void
}) {
    const [razonSocial, setRazonSocial] = useState(inicial.razonSocial)
    const [rut, setRut] = useState(inicial.rut ?? '')
    const [direccion, setDireccion] = useState(inicial.direccion ?? '')
    const [telefono, setTelefono] = useState(inicial.telefono ?? '')
    const [correo, setCorreo] = useState(inicial.correo ?? '')
    const [redondeo, setRedondeo] = useState(String(inicial.redondeoEfectivo))
    const [iva, setIva] = useState(String(inicial.ivaPorcentaje))
    const [incluyenIva, setIncluyenIva] = useState(inicial.preciosIncluyenIva)
    const [prefijo, setPrefijo] = useState(inicial.prefijoDocumentoVenta)
    const [mensaje, setMensaje] = useState(inicial.mensajeTicket ?? '')
    const [zonaHoraria, setZonaHoraria] = useState(inicial.zonaHoraria)

    // Las zonas las ofrece el servidor: solo puede elegirse una que el servidor
    // sepa resolver, porque es el que agrupa los reportes por dia.
    const zonas = usarCargar(() => apiConfiguracion.zonasHorarias(), [])

    // El impuesto al consumo se llama distinto en cada pais y la gente lo busca
    // por su nombre; "IVA" en una pantalla peruana se lee como otro impuesto.
    const impuesto = inicial.region === 'PE' ? 'IGV' : 'IVA'

    // El paso del campo de redondeo sale de los decimales de la moneda: con
    // cero decimales, ofrecer 0,10 es ofrecer algo que no se puede cobrar.
    const paso = 10 ** -inicial.decimalesMoneda

    const [error, setError] = useState<string | null>(null)
    const [guardado, setGuardado] = useState(false)
    const [enviando, setEnviando] = useState(false)

    // El aviso de guardado se retira solo: dejarlo fijo hace dudar de si el
    // ultimo cambio se grabo o es el mensaje de hace diez minutos.
    useEffect(() => {
        if (!guardado) return

        const temporizador = setTimeout(() => setGuardado(false), 4000)
        return () => clearTimeout(temporizador)
    }, [guardado])

    async function guardar(evento: FormEvent) {
        evento.preventDefault()
        setError(null)
        setGuardado(false)

        if (razonSocial.trim().length === 0) {
            setError('La razón social es obligatoria.')
            return
        }

        if (prefijo.trim().length === 0) {
            setError('El prefijo del número de venta es obligatorio.')
            return
        }

        const multiplo = Number(redondeo)
        const porcentaje = Number(iva)

        if (!Number.isFinite(multiplo) || multiplo < 0 || multiplo > 1000) {
            setError('El redondeo debe ser un número entre 0 y 1000.')
            return
        }

        // Se comprueba contra los decimales de la moneda y no contra "entero":
        // 0,10 es correcto en soles y no lo es en pesos. Redondear para
        // comparar evita que 0,1 + 0,2 arruine la validacion.
        if (Math.abs(Math.round(multiplo / paso) * paso - multiplo) > paso / 1000) {
            setError(
                inicial.decimalesMoneda === 0
                    ? 'Con esta moneda el redondeo tiene que ser un número entero.'
                    : `El redondeo admite hasta ${inicial.decimalesMoneda} decimales.`,
            )
            return
        }

        if (!Number.isFinite(porcentaje) || porcentaje < 0 || porcentaje > 100) {
            setError(`El ${impuesto} debe estar entre 0 y 100.`)
            return
        }

        setEnviando(true)

        try {
            alGuardar(
                await apiConfiguracion.actualizar({
                    razonSocial: razonSocial.trim(),
                    rut: rut.trim() || undefined,
                    direccion: direccion.trim() || undefined,
                    telefono: telefono.trim() || undefined,
                    correo: correo.trim() || undefined,
                    redondeoEfectivo: multiplo,
                    ivaPorcentaje: porcentaje,
                    preciosIncluyenIva: incluyenIva,
                    prefijoDocumentoVenta: prefijo.trim(),
                    mensajeTicket: mensaje.trim() || undefined,
                    zonaHoraria,
                }),
            )

            setGuardado(true)
        } catch (fallo) {
            setError(mensajeDeError(fallo))
        } finally {
            setEnviando(false)
        }
    }

    return (
        <form className="columna g4" onSubmit={guardar}>
            <fieldset className="sin-marco columna g4" disabled={!editable}>
                <div className="filtros">
                    <div className="filtro-ancho">
                        <label className="etiqueta" htmlFor="razon-social">
                            Razón social
                        </label>
                        <input
                            id="razon-social"
                            className="campo"
                            maxLength={160}
                            value={razonSocial}
                            onChange={(e) => setRazonSocial(e.target.value)}
                        />
                    </div>

                    <div>
                        <label className="etiqueta" htmlFor="rut-negocio">
                            RUT <span className="tenue">(opcional)</span>
                        </label>
                        <input
                            id="rut-negocio"
                            className="campo numero"
                            maxLength={20}
                            placeholder="76.543.210-K"
                            value={rut}
                            onChange={(e) => setRut(e.target.value)}
                        />
                    </div>

                    <div>
                        <label className="etiqueta" htmlFor="telefono-negocio">
                            Teléfono <span className="tenue">(opcional)</span>
                        </label>
                        <input
                            id="telefono-negocio"
                            className="campo numero"
                            type="tel"
                            maxLength={30}
                            value={telefono}
                            onChange={(e) => setTelefono(e.target.value)}
                        />
                    </div>
                </div>

                <div className="filtros">
                    <div className="filtro-ancho">
                        <label className="etiqueta" htmlFor="direccion-negocio">
                            Dirección <span className="tenue">(opcional)</span>
                        </label>
                        <input
                            id="direccion-negocio"
                            className="campo"
                            maxLength={250}
                            value={direccion}
                            onChange={(e) => setDireccion(e.target.value)}
                        />
                    </div>

                    <div>
                        <label className="etiqueta" htmlFor="correo-negocio">
                            Correo <span className="tenue">(opcional)</span>
                        </label>
                        <input
                            id="correo-negocio"
                            className="campo"
                            type="email"
                            maxLength={160}
                            value={correo}
                            onChange={(e) => setCorreo(e.target.value)}
                        />
                    </div>
                </div>

                <hr className="separador" />

                <div className="filtros">
                    <div className="filtro-ancho">
                        <label className="etiqueta" htmlFor="zona-horaria">
                            Zona horaria
                        </label>
                        <select
                            id="zona-horaria"
                            className="campo"
                            value={zonaHoraria}
                            onChange={(e) => setZonaHoraria(e.target.value)}
                        >
                            {/* La guardada va siempre, aunque el servidor ya no
                                la ofrezca: sin esto, abrir la pantalla y guardar
                                la cambiaria sin que nadie lo pidiera. */}
                            {!(zonas.datos ?? []).some((z) => z.id === zonaHoraria) && (
                                <option value={zonaHoraria}>{zonaHoraria}</option>
                            )}
                            {(zonas.datos ?? []).map((z) => (
                                <option key={z.id} value={z.id}>
                                    {z.id} ({z.desfaseActual})
                                </option>
                            ))}
                        </select>
                        <p className="tenue diminuto" style={{ marginTop: 'var(--e1)' }}>
                            Decide qué día del calendario es cada venta. Una venta de las 22:00
                            ocurre al día siguiente en hora universal, así que con la zona mal
                            puesta el reporte diario no cuadra con el cierre de caja.
                        </p>
                    </div>
                </div>

                <div className="filtros">
                    <div>
                        <label className="etiqueta" htmlFor="redondeo">
                            Redondeo del efectivo
                        </label>
                        <input
                            id="redondeo"
                            className="campo campo-numero"
                            type="number"
                            min={0}
                            max={1000}
                            step={paso}
                            inputMode="decimal"
                            value={redondeo}
                            onChange={(e) => setRedondeo(e.target.value)}
                        />
                        <p className="tenue diminuto" style={{ marginTop: 'var(--e1)' }}>
                            Múltiplo al que se lleva el efectivo, en {inicial.codigoMoneda}; cero
                            desactiva el redondeo. El redondeo{' '}
                            {inicial.redondeoHaciaAbajo
                                ? 'va siempre hacia abajo, a favor del cliente, como exige la ley del país elegido'
                                : 'busca el múltiplo más cercano y los empates suben'}
                            . <strong>Solo afecta a las ventas futuras.</strong>
                        </p>
                    </div>

                    <div>
                        <label className="etiqueta" htmlFor="iva">
                            {impuesto} (%)
                        </label>
                        <input
                            id="iva"
                            className="campo campo-numero"
                            type="number"
                            min={0}
                            max={100}
                            step="any"
                            inputMode="decimal"
                            value={iva}
                            onChange={(e) => setIva(e.target.value)}
                        />
                    </div>

                    <div>
                        <label className="etiqueta" htmlFor="prefijo">
                            Prefijo del número de venta
                        </label>
                        <input
                            id="prefijo"
                            className="campo numero"
                            maxLength={10}
                            value={prefijo}
                            onChange={(e) => setPrefijo(e.target.value)}
                        />
                    </div>
                </div>

                <label className="fila g2 interruptor">
                    <input
                        type="checkbox"
                        checked={incluyenIva}
                        onChange={(e) => setIncluyenIva(e.target.checked)}
                    />
                    <span>Los precios ya incluyen IVA</span>
                </label>
                <p className="tenue diminuto">
                    El sistema no emite documentos tributarios: el IVA solo se informa en el
                    ticket, desglosado del precio.
                </p>

                <div>
                    <label className="etiqueta" htmlFor="mensaje-ticket">
                        Mensaje del ticket <span className="tenue">(opcional)</span>
                    </label>
                    <textarea
                        id="mensaje-ticket"
                        className="campo campo-area"
                        rows={2}
                        maxLength={300}
                        placeholder="¡Gracias por su compra!"
                        value={mensaje}
                        onChange={(e) => setMensaje(e.target.value)}
                    />
                </div>
            </fieldset>

            {!editable && (
                <div className="aviso aviso-atencion">
                    Solo puede consultar estos datos. Modificarlos exige el permiso de editar la
                    configuración.
                </div>
            )}

            {error && (
                <div className="aviso aviso-error" role="alert">
                    {error}
                </div>
            )}

            {guardado && (
                <div className="aviso aviso-ok" role="status">
                    Configuración guardada.
                </div>
            )}

            <div className="fila entre envuelve g3">
                <span className="tenue diminuto">
                    Última modificación: {fechaHora(inicial.actualizadoEn)}
                </span>

                {editable && (
                    <button type="submit" className="boton boton-primario" disabled={enviando}>
                        {enviando ? 'Guardando…' : 'Guardar cambios'}
                    </button>
                )}
            </div>
        </form>
    )
}

/* -------------------------------------------------------------------------- */
/* Respaldos                                                                   */
/* -------------------------------------------------------------------------- */

/** Tamano legible. Un respaldo se mide en megabytes, no en dígitos. */
function tamano(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} kB`

    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function Respaldos() {
    const lista = usarCargar(() => apiRespaldos.listar(), [])

    const [error, setError] = useState<string | null>(null)
    const [generando, setGenerando] = useState(false)
    const [bajando, setBajando] = useState<string | null>(null)

    async function generar() {
        setError(null)
        setGenerando(true)

        try {
            await apiRespaldos.crear()
            lista.recargar()
        } catch (fallo) {
            setError(mensajeDeError(fallo))
        } finally {
            setGenerando(false)
        }
    }

    async function bajar(nombre: string) {
        setError(null)
        setBajando(nombre)

        try {
            await descargar(`/respaldos/${encodeURIComponent(nombre)}`, nombre)
        } catch (fallo) {
            setError(mensajeDeError(fallo))
        } finally {
            setBajando(null)
        }
    }

    return (
        <section className="panel columna g3">
            <div className="fila entre envuelve g3">
                <h2>Respaldos</h2>

                <button
                    type="button"
                    className="boton boton-primario"
                    onClick={generar}
                    disabled={generando}
                >
                    {generando ? 'Generando…' : 'Generar respaldo'}
                </button>
            </div>

            <p className="tenue chico">
                Cada respaldo es un volcado completo de la base. Guarde una copia{' '}
                <strong>fuera de este equipo</strong>: un respaldo en el mismo disco no sirve
                cuando el disco es el que falla.
            </p>

            {error && (
                <div className="aviso aviso-error" role="alert">
                    {error}
                </div>
            )}

            <Estado
                cargando={lista.cargando}
                error={lista.error}
                alReintentar={lista.recargar}
                vacio={(lista.datos ?? []).length === 0}
                mensajeVacio="Todavía no hay respaldos. Genere el primero antes de empezar a operar."
            >
                <div className="tabla-envoltura">
                    <table className="tabla">
                        <thead>
                            <tr>
                                <th>Archivo</th>
                                <th className="oculto-angosto">Generado</th>
                                <th className="derecha">Tamaño</th>
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {(lista.datos ?? []).map((r) => (
                                <tr key={r.nombre}>
                                    <td className="numero chico truncar">{r.nombre}</td>
                                    <td className="oculto-angosto tenue chico">
                                        {fechaHora(r.creadoEn)}
                                    </td>
                                    <td className="derecha numero">{tamano(r.tamanoBytes)}</td>
                                    <td className="derecha">
                                        <div className="fila g2 acciones-fila">
                                            <button
                                                type="button"
                                                className="boton boton-fantasma"
                                                disabled={bajando !== null}
                                                onClick={() => bajar(r.nombre)}
                                            >
                                                {bajando === r.nombre ? 'Bajando…' : 'Descargar'}
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Estado>

            <details className="plegable">
                <summary>Cómo se restaura un respaldo</summary>

                <div className="columna g2" style={{ marginTop: 'var(--e2)' }}>
                    <p className="chico">
                        La restauración <strong>no se hace desde el sistema</strong>: sobrescribe
                        todo, así que se ejecuta a mano y con la aplicación detenida. Se restaura
                        sobre una base <strong>nueva</strong> y recién después se reemplaza la que
                        está en uso: si el respaldo viniera incompleto, todavía queda la buena.
                    </p>

                    <pre className="bloque-codigo">
{`# 1. Detener la API.
# 2. Restaurar sobre una base vacia:
createdb -U postgres minimarket_restaurado
pg_restore -U postgres -d minimarket_restaurado \\
  --no-owner --no-privileges <archivo>.dump

# 3. Verificar los datos antes de reemplazar la base en uso.
# 4. Renombrar las bases y volver a levantar la API.`}
                    </pre>

                    <p className="tenue diminuto">
                        Pruebe la restauración <strong>antes</strong> de la puesta en marcha. Un
                        respaldo que nunca se restauró no es un respaldo: es un archivo.
                    </p>
                </div>
            </details>
        </section>
    )
}
